import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import finalLogo from '../assets/images/final-logo.svg';
import newPasswordBg from '../assets/images/new-password.webp';
import Button from '../components/shared/Button';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { UserService } from '../services';
import { Toast } from '../components/shared/Toast';

const styles = {
	section: 'flex flex-col lg:flex-row min-h-screen bg-bgColor',
	leftColumn:
		'w-full lg:w-1/2 flex items-center justify-center py-8 px-6 xl:pr-[243px] xl:pl-[150px]',
	imageStyle: 'h-12 w-[260px] mb-20 mx-auto lg:mx-0',
	mainHeading: 'text-left text-main leading-39 font-semibold text-dark-blue mb-3',
	subHeading: 'text-left text-lg leading-100 text-gray mb-[50px] font-medium',
	label: 'block text-base leading-100 font-medium text-dark-blue mb-3',
	rightBgImage: 'hidden lg:block relative w-1/2 bg-cover bg-center',
	carouselText: 'text-carousel leading-130 font-semibold text-white'
};

const NewPasswordPage = ({ accountEmail = 'info@writeform.com' }) => {
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const [email, setEmail] = useState(accountEmail);
	const [verificationCode, setVerificationCode] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [passwordError, setPasswordError] = useState('');

	useEffect(() => {
		const img = new Image();
		img.src = newPasswordBg;
		img.onload = () => setIsImageLoaded(true);
	}, []);

	useEffect(() => {
		// Check if user has valid state with email, verification code, and OTP verification success flag
		if (location.state && location.state.email && location.state.verificationCode && location.state.otpVerified === true) {
			setEmail(location.state.email);
			setVerificationCode(location.state.verificationCode);
			
			// Clear the state immediately after accessing it to prevent refresh access
			// This ensures one-time access - any refresh will lose the verification state
			window.history.replaceState({}, document.title, window.location.pathname);
		} else {
			// If any required state is missing or OTP verification flag is not present, redirect to forgot password
			navigate('/forgot-password', { replace: true });
		}
	}, [location.state, navigate]);

	const MIN_LEN = 8;
	const tooShort = password.length > 0 && password.length < MIN_LEN;
	// Password strength rule used for helper and validation
	const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
	

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		// Password strength validation: one lowercase, one uppercase, one number, one special char, min 8
		if (!(password.trim())) {
		    Toast.error('Please create a password.');
			setIsLoading(false);
			return;
		}
		const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;
		if (password.trim().length < 8) {
			Toast.error('Password must be at least 8 characters.');
			setIsLoading(false);
			return;
		}
		if (!passwordRegex.test(password)) {
			Toast.error('Use a mix of letters, numbers, and symbols for a stronger password');
			setIsLoading(false);
			return;
		}
		
		if (!(confirmPassword.trim())) {
		    Toast.error('Please confirm your password.');
			setIsLoading(false);
			return;
		}
		
		if (password.trim() !== confirmPassword.trim()) {
			Toast.error('Passwords don’t match. Try again.');
			setIsLoading(false);
			return;
		}

		try {
			const response = await UserService.resetPassword({ email, verificationCode, newPassword: password, confirmPassword });
			if (response.status === 200) {
				Toast.success(response.message || 'Your password has been reset successfully.');
				navigate('/login', { replace: true });
			} else {
				Toast.error(response.message || 'Failed to reset password');
			}
		} catch (error) {
			Toast.error('Network error. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={styles.section}>
			{/* Left Column (Form) */}
			<div className={styles.leftColumn}>
				<div className="w-full max-w-md lg:w-[520px]">
					{/* Back Button - match EmailVerifyPage */}
					<div className="mb-4">
						<svg
							className="cursor-pointer"
							width="40"
							height="40"
							viewBox="0 0 40 40"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							onClick={() => {
								navigate('/forgot-password');
							}}
						>
							<path
								d="M19.0625 31.25L7.8125 20L19.0625 8.75M9.375 20H32.1875"
								stroke="#180A63"
								strokeWidth="3.75"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
					<div>
						<img className={styles.imageStyle} src={finalLogo} alt="WriteForm Logo" />
						<h2 className={styles.mainHeading}>Create new password</h2>
						<p className={styles.subHeading}>
							Enter a new password for the account:{' '}
							<span className="text-dark-blue">{email}</span>
						</p>
					</div>

					<form onSubmit={handleSubmit} noValidate>
						{/* New password */}
						<div className="mb-[31px]">
							<label htmlFor="new-password" className={styles.label}>
  								New password <span className="text-red-500">*</span>
							</label>

							<div className="relative">
								<input
									id="new-password"
									name="new-password"
									type={showPassword ? 'text' : 'password'}
									autoComplete="new-password"
									required
									aria-invalid={tooShort ? 'true' : 'false'}
									className={`w-full pr-12 pl-[14px] py-[14px] border-[1.3px] rounded-[10px] placeholder-gray-400 focus:outline-none ${
										tooShort ? 'border-[#F04438]' : 'border-lightGrey'
									}`}
									placeholder="Enter new password *"
									value={password}
									onChange={(e) => { setPassword(e.target.value); if (passwordError) setPasswordError(''); }}
								/>
								<button
									type="button"
									onClick={() => setShowPassword((s) => !s)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
									className="absolute inset-y-0 right-0 px-3 flex items-center"
								>
									{showPassword ? (
										<svg
											width="23"
											height="23"
											viewBox="0 0 23 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g opacity="0.5">
												<path
													d="M11.416 5.80859C14.9982 5.80868 18.0305 8.16279 19.0498 11.4082C18.0306 14.6537 14.9983 17.0087 11.416 17.0088C7.83362 17.0088 4.80143 14.6538 3.78223 11.4082C4.80154 8.16279 7.83375 5.80859 11.416 5.80859ZM11.416 8.20898C10.5675 8.20898 9.75341 8.54557 9.15332 9.14551C8.5533 9.74553 8.21592 10.5597 8.21582 11.4082C8.21582 12.2568 8.55327 13.0708 9.15332 13.6709C9.75344 14.271 10.5673 14.6084 11.416 14.6084C12.2646 14.6083 13.0786 14.271 13.6787 13.6709C14.2786 13.0708 14.6162 12.2568 14.6162 11.4082C14.6161 10.5597 14.2787 9.74553 13.6787 9.14551C13.0786 8.5455 12.2646 8.20903 11.416 8.20898ZM11.416 9.80859C11.8403 9.80862 12.2469 9.9774 12.5469 10.2773C12.8468 10.5773 13.0155 10.984 13.0156 11.4082C13.0156 11.8325 12.8469 12.24 12.5469 12.54C12.2469 12.8399 11.8402 13.0088 11.416 13.0088C10.9917 13.0088 10.5842 12.8401 10.2842 12.54C9.98421 12.24 9.81641 11.8325 9.81641 11.4082C9.81651 10.9841 9.98437 10.5773 10.2842 10.2773C10.5842 9.97729 10.9917 9.80859 11.416 9.80859Z"
													fill="#180A63"
												/>
											</g>
										</svg>
									) : (
										<svg
											width="23"
											height="23"
											viewBox="0 0 23 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g opacity="0.5">
												<path
													d="M18.6995 18.0903L17.6389 19.1509L3.49634 5.0083L4.55688 3.94775L18.6995 18.0903ZM7.96899 10.481C7.89421 10.7539 7.85477 11.0378 7.85474 11.3257C7.85474 12.1743 8.19218 12.9883 8.79224 13.5884C9.39235 14.1885 10.2062 14.5259 11.0549 14.5259C11.3427 14.5259 11.6259 14.4853 11.8987 14.4106L13.8948 16.4067C13.0122 16.742 12.0551 16.9262 11.0549 16.9263C7.47253 16.9263 4.44034 14.5713 3.42114 11.3257C3.82335 10.0451 4.53961 8.90392 5.47876 7.99072L7.96899 10.481ZM11.0549 5.72607C14.6372 5.72616 17.6695 8.08027 18.6887 11.3257C18.2692 12.6616 17.5077 13.8458 16.5081 14.7778L14.0842 12.354C14.1958 12.0257 14.2551 11.6788 14.2551 11.3257C14.255 10.4771 13.9177 9.66301 13.3176 9.06299C12.7176 8.46298 11.9035 8.12651 11.0549 8.12646C10.7018 8.12646 10.3548 8.1851 10.0266 8.29639L8.04419 6.31396C8.97372 5.93606 9.98964 5.72607 11.0549 5.72607ZM10.0842 12.5962C10.0281 12.5533 9.97356 12.508 9.9231 12.4575C9.87263 12.407 9.82724 12.3525 9.78442 12.2964L10.0842 12.5962ZM11.5286 9.79834C11.7743 9.87466 12.0004 10.0095 12.1858 10.1948C12.3708 10.3798 12.5049 10.6058 12.5813 10.8511L11.5286 9.79834Z"
													fill="#180A63"
												/>
											</g>
										</svg>
									)}
								</button>
							</div>
							<p className="mt-2 text-xs font-medium text-gray">
								Must be at least 8 characters and include uppercase, lowercase, number, and special character.
							</p>
							
						</div>

						{/* Confirm password */}
						<div className="mb-[60px]">
							<label htmlFor="confirm-password" className={styles.label}>
								Confirm new password <span className="text-red-500">*</span>
							</label>
							<div className="relative">
								<input
									id="confirm-password"
									name="confirm-password"
									type={showConfirmPassword ? 'text' : 'password'}
									autoComplete="new-password"
									required
									aria-invalid={mismatch ? 'true' : 'false'}
									className={`w-full pr-12 pl-[14px] py-[14px] border-[1.3px] rounded-[10px] placeholder-gray-400 focus:outline-none ${
										mismatch ? 'border-[#F04438]' : 'border-lightGrey'
									}`}
									placeholder="Re-enter password *"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
								/>
								<button
									type="button"
									onClick={() => setShowConfirmPassword((s) => !s)}
									aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
									className="absolute inset-y-0 right-0 px-3 flex items-center"
								>
									{/* same eye icons as above */}
									{showConfirmPassword ? (
										<svg
											width="23"
											height="23"
											viewBox="0 0 23 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g opacity="0.5">
												<path
													d="M11.416 5.80859C14.9982 5.80868 18.0305 8.16279 19.0498 11.4082C18.0306 14.6537 14.9983 17.0087 11.416 17.0088C7.83362 17.0088 4.80143 14.6538 3.78223 11.4082C4.80154 8.16279 7.83375 5.80859 11.416 5.80859ZM11.416 8.20898C10.5675 8.20898 9.75341 8.54557 9.15332 9.14551C8.5533 9.74553 8.21592 10.5597 8.21582 11.4082C8.21582 12.2568 8.55327 13.0708 9.15332 13.6709C9.75344 14.271 10.5673 14.6084 11.416 14.6084C12.2646 14.6083 13.0786 14.271 13.6787 13.6709C14.2786 13.0708 14.6162 12.2568 14.6162 11.4082C14.6161 10.5597 14.2787 9.74553 13.6787 9.14551C13.0786 8.5455 12.2646 8.20903 11.416 8.20898ZM11.416 9.80859C11.8403 9.80862 12.2469 9.9774 12.5469 10.2773C12.8468 10.5773 13.0155 10.984 13.0156 11.4082C13.0156 11.8325 12.8469 12.24 12.5469 12.54C12.2469 12.8399 11.8402 13.0088 11.416 13.0088C10.9917 13.0088 10.5842 12.8401 10.2842 12.54C9.98421 12.24 9.81641 11.8325 9.81641 11.4082C9.81651 10.9841 9.98437 10.5773 10.2842 10.2773C10.5842 9.97729 10.9917 9.80859 11.416 9.80859Z"
													fill="#180A63"
												/>
											</g>
										</svg>
									) : (
										<svg
											width="23"
											height="23"
											viewBox="0 0 23 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g opacity="0.5">
												<path
													d="M18.6995 18.0903L17.6389 19.1509L3.49634 5.0083L4.55688 3.94775L18.6995 18.0903ZM7.96899 10.481C7.89421 10.7539 7.85477 11.0378 7.85474 11.3257C7.85474 12.1743 8.19218 12.9883 8.79224 13.5884C9.39235 14.1885 10.2062 14.5259 11.0549 14.5259C11.3427 14.5259 11.6259 14.4853 11.8987 14.4106L13.8948 16.4067C13.0122 16.742 12.0551 16.9262 11.0549 16.9263C7.47253 16.9263 4.44034 14.5713 3.42114 11.3257C3.82335 10.0451 4.53961 8.90392 5.47876 7.99072L7.96899 10.481ZM11.0549 5.72607C14.6372 5.72616 17.6695 8.08027 18.6887 11.3257C18.2692 12.6616 17.5077 13.8458 16.5081 14.7778L14.0842 12.354C14.1958 12.0257 14.2551 11.6788 14.2551 11.3257C14.255 10.4771 13.9177 9.66301 13.3176 9.06299C12.7176 8.46298 11.9035 8.12651 11.0549 8.12646C10.7018 8.12646 10.3548 8.1851 10.0266 8.29639L8.04419 6.31396C8.97372 5.93606 9.98964 5.72607 11.0549 5.72607ZM10.0842 12.5962C10.0281 12.5533 9.97356 12.508 9.9231 12.4575C9.87263 12.407 9.82724 12.3525 9.78442 12.2964L10.0842 12.5962ZM11.5286 9.79834C11.7743 9.87466 12.0004 10.0095 12.1858 10.1948C12.3708 10.3798 12.5049 10.6058 12.5813 10.8511L11.5286 9.79834Z"
													fill="#180A63"
												/>
											</g>
										</svg>
									)}
								</button>
							</div>
							
						</div>

						<div>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? 'Updating...' : 'Reset password'}
							</Button>
						</div>
					</form>
				</div>
			</div>

			{/* Right Column (Image and Text) */}
			<div
				className={styles.rightBgImage}
				style={isImageLoaded ? { backgroundImage: `url(${newPasswordBg})` } : {}}
			>
				{!isImageLoaded ? (
					<div className="absolute inset-0">
						<Skeleton
							height="100%"
							width="100%"
							baseColor="#E5E7EB"
							highlightColor="#F3F4F6"
							duration={1.2}
						/>
					</div>
				) : (
					<div className="absolute inset-0 bg-opacity-40 flex flex-col justify-end p-8">
						<div className="text-white bg-white/5 rounded-lg p-6 mx-4 backdrop-blur-30 border-2 border-white/10">
							<p className={styles.carouselText}>
								From your first word to the final format, WriteForm helps you craft clean, structured,
								and impactful documents — without the usual formatting struggle.
							</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default NewPasswordPage;
